/**
 * Activation Life — post-onboarding activation flow (spec Phase 1).
 *
 * Rendered inside the wizard frame right after the Wheel of Life:
 *   A2 — pick up to 3 priority domains (pre-selected from the diagnostic)
 *   A3a — identify core values (guided: curated chips + free additions)
 *   A3b — 10-year vision per priority domain ("où veux-tu être dans 10 ans ?")
 *   A3c — mission statement: Life drafts it from the values + visions, the
 *         user edits and validates it; it is saved into their Notes and then
 *         transformed into the roadmap proposal
 *   A4 — "Voici ce que Life a compris": editable plan proposal
 *   A5 — success: the Life Plan exists, first perceived result
 *   A6 — orientation: feed / apps / Life Intelligence / search
 *
 * The flow is resumable (AC08): on mount it reads /api/activation/status/
 * and jumps to the persisted step. It NEVER dead-ends (AC23): an empty or
 * failed AI call still yields a deterministic mission draft and plan
 * proposal server-side.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { makeGetRequest, makePostRequest } from 'utils/api'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import NatureIcon from '@mui/icons-material/Nature'
import PeopleIcon from '@mui/icons-material/People'
import WorkIcon from '@mui/icons-material/Work'
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement'
import HomeIcon from '@mui/icons-material/Home'
import DescriptionIcon from '@mui/icons-material/Description'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed'
import AppsIcon from '@mui/icons-material/Apps'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SearchIcon from '@mui/icons-material/Search'

const DOMAIN_ICONS = {
  FINANCE: AccountBalanceIcon,
  TIME: AccessTimeIcon,
  FOOTPRINT: NatureIcon,
  SOCIAL: PeopleIcon,
  PROFESSIONAL: WorkIcon,
  PERSONAL: SelfImprovementIcon,
  HOME: HomeIcon,
  ADMIN: DescriptionIcon,
  HEALTH: FavoriteBorderIcon,
  OTHER: StarBorderIcon,
}

// Selectable domains on screen A2 (Roadmap codes). HEALTH has no onboarding
// score yet but stays selectable — the AI parser can still target it.
const SELECTABLE_DOMAINS = [
  'FINANCE',
  'TIME',
  'PERSONAL',
  'SOCIAL',
  'PROFESSIONAL',
  'HOME',
  'FOOTPRINT',
  'ADMIN',
  'HEALTH',
]

// Same visual language as the wizard's getButtonStyles('primary'/'outlined')
// (UserWizardPage) — kept local so this component stays self-contained.
const primaryButtonSx = {
  fontWeight: 700,
  borderRadius: 2,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  backgroundColor: '#0089ff',
  background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
  color: 'white',
  boxShadow: '0 4px 12px rgba(0, 137, 255, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0070cc 0%, #0089ff 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 137, 255, 0.4)',
  },
  '&.Mui-disabled': {
    background: 'rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.4)',
  },
}

const ghostButtonSx = {
  fontWeight: 600,
  borderRadius: 2,
  textTransform: 'none',
  color: 'rgba(255,255,255,0.7)',
  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
}

const cardBaseSx = {
  borderRadius: 3,
  border: '1px solid rgba(255,255,255,0.12)',
  backgroundColor: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.25s ease',
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.05)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#0089ff' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.45)',
    opacity: 1,
  },
}

// 'welcome' is the final orientation screen: it tells the user they are about
// to land on the feed, and that they can also navigate the Life apps
// classically, chat with Life Intelligence, or use the global search.
const STEP_ORDER = ['priorities', 'values', 'vision', 'mission', 'plan', 'done', 'welcome']

const stateToStep = (state) => {
  if (state === 'PRIORITIES_SELECTED') return 'values'
  if (state === 'MISSION_PROPOSED') return 'mission'
  if (state === 'PLAN_PROPOSED') return 'plan'
  if (state === 'ACTIVATED') return 'done'
  return 'priorities'
}

// Curated core values (A3a). Codes are i18n keys under activation.values.list;
// the translated labels are what the user sees AND what is sent to the
// backend, so the mission statement speaks their language.
const VALUE_KEYS = [
  'family',
  'health',
  'freedom',
  'security',
  'love',
  'friendship',
  'learning',
  'creativity',
  'adventure',
  'nature',
  'spirituality',
  'integrity',
  'generosity',
  'achievement',
  'independence',
  'balance',
  'transmission',
  'pleasure',
]
const MAX_VALUES = 5

const scoreColor = (score) => {
  if (score === null || score === undefined) return 'rgba(255,255,255,0.35)'
  if (score < 4) return '#ff6b6b'
  if (score < 6.5) return '#ffb74d'
  return '#4dd882'
}

const ActivationFlow = ({ onFinish }) => {
  const { t } = useTranslation('layout')
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState('priorities')
  const [domainScores, setDomainScores] = useState([])
  const [suggested, setSuggested] = useState([])
  const [maxPriorities, setMaxPriorities] = useState(3)
  const [selected, setSelected] = useState([])
  const [values, setValues] = useState([])
  const [customValue, setCustomValue] = useState('')
  const [visions, setVisions] = useState({})
  // Domaines ajoutés librement par l'utilisateur en plus de ses 3 priorités :
  // l'énoncé de mission ne doit pas se limiter aux domaines en lacune.
  const [extraDomains, setExtraDomains] = useState([])
  const [mission, setMission] = useState('')
  const [missionAiUsed, setMissionAiUsed] = useState(false)
  const [goals, setGoals] = useState([])
  const [aiUsed, setAiUsed] = useState(false)
  const [createdGoals, setCreatedGoals] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // --- resume (AC08) -------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const resp = await makeGetRequest('/api/activation/status/', null, true)
        if (cancelled) return
        const data = resp?.data
        if (resp?.status === 200 && data) {
          setDomainScores(Array.isArray(data.domain_scores) ? data.domain_scores : [])
          const sugg = Array.isArray(data.suggested_priorities) ? data.suggested_priorities : []
          setSuggested(sugg.map((s) => s.domain))
          setMaxPriorities(data.max_priorities || 3)
          const alreadySelected = Array.isArray(data.selected_priorities)
            ? data.selected_priorities
            : []
          // Pre-select the diagnostic's suggestions on a fresh session (AC02).
          setSelected(alreadySelected.length ? alreadySelected : sugg.map((s) => s.domain))
          setValues(Array.isArray(data.values) ? data.values : [])
          const storedVisions =
            data.visions && typeof data.visions === 'object' ? data.visions : {}
          setVisions(storedVisions)
          const priorityCodes = alreadySelected.length
            ? alreadySelected
            : sugg.map((s) => s.domain)
          setExtraDomains(
            Object.keys(storedVisions).filter((d) => !priorityCodes.includes(d)),
          )
          setMission(data.mission_statement || '')
          if (data.proposal?.goals?.length) {
            setGoals(data.proposal.goals)
            setAiUsed(data.proposal.source === 'ai')
          }
          setStep(stateToStep(data.state))
        }
      } catch (err) {
        console.error('ActivationFlow: status load failed', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const scoreByDomain = useMemo(() => {
    const map = {}
    domainScores.forEach((row) => {
      map[row.domain] = row.score
    })
    return map
  }, [domainScores])

  const stepIndex = STEP_ORDER.indexOf(step)

  // --- actions --------------------------------------------------------------
  const toggleDomain = (code) => {
    setSelected((prev) => {
      if (prev.includes(code)) return prev.filter((d) => d !== code)
      if (prev.length >= maxPriorities) return prev
      return [...prev, code]
    })
  }

  const submitPriorities = async () => {
    if (submitting || !selected.length) return
    setSubmitting(true)
    setError('')
    try {
      const resp = await makePostRequest(
        '/api/activation/priorities/',
        { domains: selected },
        null,
        true,
      )
      if (resp?.status === 200) {
        setSelected(resp.data.selected_priorities || selected)
        setStep('values')
      } else {
        setError(t('activation.errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // --- values (A3a) ---------------------------------------------------------
  const toggleValue = (label) => {
    setValues((prev) => {
      if (prev.includes(label)) return prev.filter((v) => v !== label)
      if (prev.length >= MAX_VALUES) return prev
      return [...prev, label]
    })
  }

  const addCustomValue = () => {
    const label = customValue.trim()
    if (!label) return
    setCustomValue('')
    toggleValue(label)
  }

  // --- vision (A3b) ---------------------------------------------------------
  // Les priorités d'abord, puis les domaines que l'utilisateur a ajoutés.
  const visionDomains = [...selected, ...extraDomains.filter((d) => !selected.includes(d))]
  const addableDomains = SELECTABLE_DOMAINS.filter((d) => !visionDomains.includes(d))

  const addVisionDomain = (code) => {
    setExtraDomains((prev) => (prev.includes(code) ? prev : [...prev, code]))
  }

  const removeVisionDomain = (code) => {
    setExtraDomains((prev) => prev.filter((d) => d !== code))
    setVisions((prev) => {
      const next = { ...prev }
      delete next[code]
      return next
    })
  }

  const visionExample = (code) =>
    t(`activation.vision.examples.${code}`, {
      defaultValue: t('activation.vision.placeholder'),
    })

  // --- mission (A3c) --------------------------------------------------------
  const draftMission = async () => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      const resp = await makePostRequest(
        '/api/activation/mission/draft/',
        { values, visions },
        null,
        true,
      )
      if (resp?.status === 200 && resp.data?.mission_statement) {
        setMission(resp.data.mission_statement)
        setMissionAiUsed(Boolean(resp.data.ai_used))
        setStep('mission')
      } else {
        setError(t('activation.errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const confirmMission = async () => {
    if (submitting || !mission.trim()) return
    setSubmitting(true)
    setError('')
    try {
      const resp = await makePostRequest(
        '/api/activation/mission/confirm/',
        { mission: mission.trim() },
        null,
        true,
      )
      if (resp?.status === 200 && resp.data?.proposal?.goals?.length) {
        setGoals(resp.data.proposal.goals)
        setAiUsed(Boolean(resp.data.ai_used))
        setStep('plan')
      } else {
        setError(t('activation.errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const confirmPlan = async () => {
    if (submitting || !goals.length) return
    setSubmitting(true)
    setError('')
    try {
      const resp = await makePostRequest(
        '/api/activation/confirm/',
        { goals },
        null,
        true,
      )
      if (resp?.status === 201) {
        setCreatedGoals(resp.data.created_goals || [])
        setStep('done')
      } else {
        setError(t('activation.errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const skipActivation = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await makePostRequest('/api/activation/skip/', {}, null, true)
    } finally {
      // Even when skipping, show the orientation screen so the user knows
      // what the feed is and how else they can navigate Life.
      setSubmitting(false)
      setStep('welcome')
    }
  }

  const updateGoalTitle = (index, title) => {
    setGoals((prev) => prev.map((g, i) => (i === index ? { ...g, title } : g)))
  }

  const removeGoal = (index) => {
    setGoals((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
  }

  // --- shared UI ------------------------------------------------------------
  const domainLabel = (code) =>
    t(`activation.domains.${code}`, { defaultValue: code })

  const renderProgress = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
      {STEP_ORDER.map((s, i) => (
        <Box
          key={s}
          sx={{
            width: i === stepIndex ? 28 : 8,
            height: 8,
            borderRadius: 4,
            transition: 'all 0.3s ease',
            background:
              i <= stepIndex
                ? 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)'
                : 'rgba(255,255,255,0.18)',
          }}
        />
      ))}
    </Box>
  )

  if (loading) {
    return (
      <Box
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
      >
        <CircularProgress sx={{ color: '#0089ff' }} />
      </Box>
    )
  }

  // --- A2 : priorities ------------------------------------------------------
  const renderPriorities = () => (
    <Fade in timeout={500}>
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.priorities.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 4, lineHeight: 1.6 }}
        >
          {t('activation.priorities.intro')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 1.5,
            mb: 2,
          }}
        >
          {SELECTABLE_DOMAINS.map((code) => {
            const Icon = DOMAIN_ICONS[code] || StarBorderIcon
            const score = scoreByDomain[code]
            const isSelected = selected.includes(code)
            const isSuggested = suggested.includes(code)
            const isFull = !isSelected && selected.length >= maxPriorities
            return (
              <Box
                key={code}
                onClick={() => !isFull && toggleDomain(code)}
                sx={{
                  ...cardBaseSx,
                  p: 2,
                  cursor: isFull ? 'default' : 'pointer',
                  opacity: isFull ? 0.45 : 1,
                  position: 'relative',
                  ...(isSelected && {
                    border: '1px solid #0089ff',
                    backgroundColor: 'rgba(0,137,255,0.12)',
                    boxShadow: '0 4px 16px rgba(0,137,255,0.25)',
                  }),
                  '&:hover': !isFull
                    ? { transform: 'translateY(-2px)', borderColor: 'rgba(0,168,255,0.6)' }
                    : {},
                }}
              >
                {isSuggested && (
                  <Chip
                    label={t('activation.priorities.suggested')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 10,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'white',
                      background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                  <Icon sx={{ fontSize: 22, color: isSelected ? '#00a8ff' : 'rgba(255,255,255,0.75)' }} />
                  <Typography fontWeight={600} sx={{ flex: 1, fontSize: 15 }}>
                    {domainLabel(code)}
                  </Typography>
                  {isSelected && <CheckCircleIcon sx={{ fontSize: 20, color: '#00a8ff' }} />}
                </Box>
                {score !== null && score !== undefined ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        flex: 1,
                        height: 5,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.min(100, Math.max(4, score * 10))}%`,
                          height: '100%',
                          borderRadius: 3,
                          backgroundColor: scoreColor(score),
                          transition: 'width 0.6s ease',
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', minWidth: 34 }}>
                      {Number(score).toFixed(1)}/10
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
                    {t('activation.priorities.noScore')}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>

        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 3 }}>
          {t('activation.priorities.counter', { n: selected.length, max: maxPriorities })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            disabled={!selected.length || submitting}
            onClick={submitPriorities}
            sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 16 }}
          >
            {t('activation.priorities.cta')}
          </Button>
          <Button onClick={skipActivation} disabled={submitting} sx={ghostButtonSx}>
            {t('activation.skip')}
          </Button>
        </Box>
      </Box>
    </Fade>
  )

  // --- A3a : core values ----------------------------------------------------
  const renderValues = () => (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.values.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 3.5, lineHeight: 1.6 }}
        >
          {t('activation.values.intro')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {VALUE_KEYS.map((key) => {
            const label = t(`activation.values.list.${key}`)
            const isSelected = values.includes(label)
            const isFull = !isSelected && values.length >= MAX_VALUES
            return (
              <Chip
                key={key}
                label={label}
                clickable={!isFull}
                onClick={() => !isFull && toggleValue(label)}
                icon={isSelected ? <CheckCircleIcon sx={{ fontSize: 16, color: '#00a8ff !important' }} /> : undefined}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  px: 0.5,
                  border: isSelected
                    ? '1px solid #0089ff'
                    : '1px solid rgba(255,255,255,0.2)',
                  backgroundColor: isSelected
                    ? 'rgba(0,137,255,0.18)'
                    : 'rgba(255,255,255,0.05)',
                  opacity: isFull ? 0.45 : 1,
                  transition: 'all 0.2s ease',
                  '&:hover': !isFull ? { backgroundColor: 'rgba(0,137,255,0.12)' } : {},
                }}
              />
            )
          })}
          {/* Custom values the user added (not in the curated list) */}
          {values
            .filter((v) => !VALUE_KEYS.some((k) => t(`activation.values.list.${k}`) === v))
            .map((v) => (
              <Chip
                key={v}
                label={v}
                clickable
                onClick={() => toggleValue(v)}
                icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#00a8ff !important' }} />}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  px: 0.5,
                  border: '1px solid #0089ff',
                  backgroundColor: 'rgba(0,137,255,0.18)',
                }}
              />
            ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1.5, maxWidth: 420, mx: 'auto' }}>
          <TextField
            size="small"
            fullWidth
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addCustomValue()
              }
            }}
            placeholder={t('activation.values.customPlaceholder')}
            disabled={values.length >= MAX_VALUES}
            sx={textFieldSx}
          />
          <Button
            onClick={addCustomValue}
            disabled={!customValue.trim() || values.length >= MAX_VALUES}
            sx={{ ...ghostButtonSx, whiteSpace: 'nowrap' }}
          >
            {t('activation.values.addCustom')}
          </Button>
        </Box>

        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 3 }}>
          {t('activation.values.counter', { n: values.length, max: MAX_VALUES })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            disabled={!values.length}
            onClick={() => setStep('vision')}
            sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 16 }}
          >
            {t('activation.values.cta')}
          </Button>
          <Button
            onClick={() => setStep('priorities')}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ ...ghostButtonSx, fontSize: 13 }}
          >
            {t('activation.back')}
          </Button>
        </Box>
      </Box>
    </Fade>
  )

  // --- A3b : 10-year vision per priority domain -----------------------------
  const renderVision = () => (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.vision.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 3.5, lineHeight: 1.6 }}
        >
          {t('activation.vision.intro')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {visionDomains.map((code) => {
            const Icon = DOMAIN_ICONS[code] || StarBorderIcon
            const isExtra = !selected.includes(code)
            const example = visionExample(code)
            return (
              <Box key={code} sx={{ ...cardBaseSx, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                  <Icon sx={{ fontSize: 20, color: '#00a8ff' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1 }}>
                    {t('activation.vision.question', { domain: domainLabel(code) })}
                  </Typography>
                  {isExtra && (
                    <IconButton
                      size="small"
                      onClick={() => removeVisionDomain(code)}
                      aria-label={t('activation.vision.removeDomain')}
                      sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff6b6b' } }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  value={visions[code] || ''}
                  onChange={(e) =>
                    setVisions((prev) => ({ ...prev, [code]: e.target.value }))
                  }
                  placeholder={example}
                  disabled={submitting}
                  sx={textFieldSx}
                />
                {!String(visions[code] || '').trim() && (
                  <Button
                    size="small"
                    onClick={() => setVisions((prev) => ({ ...prev, [code]: example }))}
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                    sx={{ ...ghostButtonSx, mt: 0.75, fontSize: 12.5 }}
                  >
                    {t('activation.vision.useExample')}
                  </Button>
                )}
              </Box>
            )
          })}
        </Box>

        {/* L'énoncé de mission ne se limite pas aux 3 domaines en lacune :
            l'utilisateur ajoute librement les domaines qui comptent pour lui. */}
        {addableDomains.length > 0 && (
          <Box sx={{ ...cardBaseSx, p: 2, mb: 3, backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14.5, mb: 0.5 }}>
              {t('activation.vision.addDomainTitle')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 1.5 }}>
              {t('activation.vision.addDomainHint')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {addableDomains.map((code) => {
                const Icon = DOMAIN_ICONS[code] || StarBorderIcon
                return (
                  <Chip
                    key={code}
                    clickable
                    onClick={() => addVisionDomain(code)}
                    icon={<Icon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.75) !important' }} />}
                    label={`+ ${domainLabel(code)}`}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      border: '1px solid rgba(255,255,255,0.2)',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      '&:hover': { backgroundColor: 'rgba(0,137,255,0.15)', borderColor: 'rgba(0,168,255,0.6)' },
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        )}

        {submitting ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={32} sx={{ color: '#00a8ff' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {t('activation.mission.drafting')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              disabled={!Object.values(visions).some((v) => String(v || '').trim())}
              onClick={draftMission}
              startIcon={<AutoAwesomeIcon />}
              sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 16 }}
            >
              {t('activation.vision.cta')}
            </Button>
            <Button onClick={draftMission} sx={ghostButtonSx}>
              {t('activation.vision.skip')}
            </Button>
            <Button
              onClick={() => setStep('values')}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{ ...ghostButtonSx, fontSize: 13 }}
            >
              {t('activation.back')}
            </Button>
          </Box>
        )}
      </Box>
    </Fade>
  )

  // --- A3c : mission statement review ---------------------------------------
  const renderMission = () => (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.mission.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 3, lineHeight: 1.6 }}
        >
          {missionAiUsed ? t('activation.mission.introAi') : t('activation.mission.introFallback')}
        </Typography>

        <Box sx={{ ...cardBaseSx, p: 0.5, mb: 1.5, border: '1px solid rgba(0,137,255,0.4)' }}>
          <TextField
            multiline
            minRows={6}
            fullWidth
            value={mission}
            onChange={(e) => setMission(e.target.value)}
            disabled={submitting}
            InputProps={{
              sx: {
                color: 'white',
                fontSize: 15.5,
                lineHeight: 1.7,
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 1, textAlign: 'center' }}>
          {t('activation.mission.editHint')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 3, textAlign: 'center', fontStyle: 'italic' }}>
          {t('activation.mission.noteInfo')}
        </Typography>

        {submitting ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={32} sx={{ color: '#00a8ff' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {t('activation.mission.transforming')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              disabled={!mission.trim()}
              onClick={confirmMission}
              sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 16 }}
            >
              {t('activation.mission.cta')}
            </Button>
            <Button onClick={draftMission} startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />} sx={ghostButtonSx}>
              {t('activation.mission.regenerate')}
            </Button>
            <Button
              onClick={() => setStep('vision')}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{ ...ghostButtonSx, fontSize: 13 }}
            >
              {t('activation.back')}
            </Button>
          </Box>
        )}
      </Box>
    </Fade>
  )

  // --- A4 : plan proposal ---------------------------------------------------
  const renderPlan = () => (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.plan.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 3, lineHeight: 1.6 }}
        >
          {aiUsed ? t('activation.plan.introAi') : t('activation.plan.introFallback')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {goals.map((goal, index) => {
            const Icon = DOMAIN_ICONS[goal.domain] || StarBorderIcon
            return (
              <Box key={index} sx={{ ...cardBaseSx, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      background: 'linear-gradient(135deg, rgba(0,137,255,0.25) 0%, rgba(0,168,255,0.15) 100%)',
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: '#00a8ff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={goal.title || ''}
                      onChange={(e) => updateGoalTitle(index, e.target.value)}
                      InputProps={{
                        disableUnderline: false,
                        sx: {
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 15,
                          '&:before': { borderColor: 'transparent' },
                          '&:hover:not(.Mui-disabled):before': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&:after': { borderColor: '#0089ff' },
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        label={domainLabel(goal.domain)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          color: '#00a8ff',
                          backgroundColor: 'rgba(0,137,255,0.12)',
                        }}
                      />
                      {goal.description ? (
                        <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
                          {goal.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  {goals.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeGoal(index)}
                      sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff6b6b' } }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>

        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 3 }}>
          {t('activation.plan.editHint')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            disabled={submitting || !goals.length || goals.some((g) => !String(g.title || '').trim())}
            onClick={confirmPlan}
            sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 16 }}
          >
            {submitting ? t('activation.plan.creating') : t('activation.plan.cta')}
          </Button>
          <Button
            onClick={() => setStep('mission')}
            disabled={submitting}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={ghostButtonSx}
          >
            {t('activation.plan.rephrase')}
          </Button>
        </Box>
      </Box>
    </Fade>
  )

  // --- A5 : done / first result --------------------------------------------
  const renderDone = () => (
    <Fade in timeout={600}>
      <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            mx: 'auto',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(0,137,255,0.3) 0%, rgba(0,168,255,0.15) 100%)',
            boxShadow: '0 0 40px rgba(0,137,255,0.35)',
            animation: 'activationPulse 2s ease-in-out infinite',
            '@keyframes activationPulse': {
              '0%, 100%': { boxShadow: '0 0 24px rgba(0,137,255,0.25)' },
              '50%': { boxShadow: '0 0 48px rgba(0,137,255,0.5)' },
            },
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 48, color: '#00a8ff' }} />
        </Box>

        <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('activation.done.title')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
          {t('activation.done.body', { n: createdGoals.length || goals.length })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
          {(createdGoals.length ? createdGoals : goals).map((goal, index) => {
            const Icon = DOMAIN_ICONS[goal.domain] || StarBorderIcon
            return (
              <Box
                key={goal.id || index}
                sx={{ ...cardBaseSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left' }}
              >
                <Icon sx={{ fontSize: 20, color: '#00a8ff', flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: 14.5, flex: 1 }}>
                  {goal.title}
                </Typography>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#4dd882', flexShrink: 0 }} />
              </Box>
            )
          })}
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13.5, mb: 3, fontStyle: 'italic' }}>
          {t('activation.done.note')}
        </Typography>

        <Button
          variant="contained"
          onClick={() => setStep('welcome')}
          sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 17 }}
        >
          {t('activation.done.cta')}
        </Button>
      </Box>
    </Fade>
  )

  // --- A6 : orientation — how to navigate Life -------------------------------
  const WELCOME_MODES = [
    { key: 'feed', Icon: DynamicFeedIcon },
    { key: 'apps', Icon: AppsIcon },
    { key: 'chat', Icon: ChatBubbleOutlineIcon },
    { key: 'search', Icon: SearchIcon },
  ]

  const renderWelcome = () => (
    <Fade in timeout={600}>
      <Box sx={{ maxWidth: 620, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center', mb: 1.5 }}>
          {t('activation.welcome.title')}
        </Typography>
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 4, lineHeight: 1.6 }}
        >
          {t('activation.welcome.intro')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
          {WELCOME_MODES.map(({ key, Icon }, index) => (
            <Fade in timeout={400 + index * 200} key={key}>
              <Box
                sx={{
                  ...cardBaseSx,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.75,
                  ...(key === 'feed' && {
                    border: '1px solid rgba(0,137,255,0.5)',
                    backgroundColor: 'rgba(0,137,255,0.08)',
                  }),
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background:
                      'linear-gradient(135deg, rgba(0,137,255,0.25) 0%, rgba(0,168,255,0.15) 100%)',
                  }}
                >
                  <Icon sx={{ fontSize: 22, color: '#00a8ff' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                      {t(`activation.welcome.${key}.title`)}
                    </Typography>
                    {key === 'feed' && (
                      <Chip
                        label={t('activation.welcome.feed.badge')}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'white',
                          background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                    {t(`activation.welcome.${key}.body`)}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => onFinish?.()}
            sx={{ ...primaryButtonSx, minWidth: 280, py: 1.5, px: 4, fontSize: 17 }}
          >
            {t('activation.welcome.cta')}
          </Button>
        </Box>
      </Box>
    </Fade>
  )

  return (
    <Box color="white" px={{ xs: 2, sm: 4 }} py={4} maxWidth={860} mx="auto">
      {renderProgress()}
      {error && (
        <Typography sx={{ color: '#ff6b6b', textAlign: 'center', mb: 2, fontSize: 14 }}>
          {error}
        </Typography>
      )}
      {step === 'priorities' && renderPriorities()}
      {step === 'values' && renderValues()}
      {step === 'vision' && renderVision()}
      {step === 'mission' && renderMission()}
      {step === 'plan' && renderPlan()}
      {step === 'done' && renderDone()}
      {step === 'welcome' && renderWelcome()}
    </Box>
  )
}

export default ActivationFlow
